import { FunctionComponent, useEffect, useState } from 'react';
import { Box, Button, FormField, Header, Modal, Select, SelectProps, SpaceBetween, Textarea } from '@amzn/awsui-components-react';
import { Order } from '@amzn/ito-client';
import { getString } from 'common/ui-string-labels/ui-strings-utils';
import { buildOrderReferenceBadges } from 'common/utils/order-reference-badges';

const OTHER_REASON_VALUE = 'OTHER';

function buildReasonOptions(): SelectProps.Option[] {
    return [
        { value: 'COMPLETED_BY_MISTAKE', label: getString('bulkOrders.submittedOrders.reopenReasonCompletedByMistake') },
        { value: 'RESELLER_LICENSES_WRONG', label: getString('bulkOrders.submittedOrders.reopenReasonResellerLicensesWrong') },
        { value: OTHER_REASON_VALUE, label: getString('bulkOrders.submittedOrders.reopenReasonOther') },
    ];
}

export interface ReopenBulkOrderModalProps {
    visible: boolean;
    order?: Order;
    isProcessing: boolean;
    onDismiss: () => void;
    /** Present only when more than one order is queued; shows a Skip button and advances without reopening. */
    onSkip?: () => void;
    /** The reason text to record: the selected option's label, or the free-text entry when "Other" is chosen. */
    onConfirm: (reason: string) => void;
    /** Shows "(current/total)" in the modal title; omit for the single-order flow. */
    progress?: { current: number; total: number };
}

export const ReopenBulkOrderModal: FunctionComponent<ReopenBulkOrderModalProps> = ({
    visible, order, isProcessing, onDismiss, onSkip, onConfirm, progress,
}) => {
    const reasonOptions = buildReasonOptions();
    const [selectedOption, setSelectedOption] = useState<SelectProps.Option | null>(null);
    const [otherReason, setOtherReason] = useState('');

    useEffect(() => {
        if (visible) {
            setSelectedOption(null);
            setOtherReason('');
        }
    }, [visible, order]);

    const isOtherSelected = selectedOption?.value === OTHER_REASON_VALUE;
    const isValid = selectedOption !== null && (!isOtherSelected || otherReason.trim() !== '');

    const confirm = () => {
        if (!isValid || !selectedOption) {
            return;
        }
        const reason = isOtherSelected ? otherReason.trim() : (selectedOption.label as string);
        onConfirm(reason);
    };

    return (
        <Modal
            data-testid="reopen-bulk-order-modal"
            visible={visible}
            onDismiss={onDismiss}
            header={
                <Header
                    variant="h2"
                    description={<SpaceBetween direction="horizontal" size="xs">{buildOrderReferenceBadges(order)}</SpaceBetween>}
                >
                    {getString('bulkOrders.submittedOrders.reopenOrder')}
                    {progress ? ` (${progress.current}/${progress.total})` : ''}
                </Header>
            }
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        {onSkip && (
                            <Button data-testid="reopen-bulk-order-skip" variant="link" onClick={onSkip} disabled={isProcessing}>{getString('bulkOrders.submittedOrders.queueSkip')}</Button>
                        )}
                        <Button data-testid="reopen-bulk-order-back" variant="link" onClick={onDismiss} disabled={isProcessing}>{getString('common.preferences.cancelLabel')}</Button>
                        <Button data-testid="reopen-bulk-order-confirm" variant="primary" onClick={confirm} disabled={!isValid} loading={isProcessing}>
                            {getString('bulkOrders.submittedOrders.reopenOrder')}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween direction="vertical" size="s">
                <Box>{getString('bulkOrders.submittedOrders.reopenConfirmMessage')}</Box>
                <FormField label={getString('bulkOrders.submittedOrders.reopenReasonFieldLabel')}>
                    <Select
                        data-testid="reopen-bulk-order-reason-select"
                        selectedOption={selectedOption}
                        onChange={({ detail }) => setSelectedOption(detail.selectedOption)}
                        options={reasonOptions}
                        placeholder={getString('bulkOrders.submittedOrders.reopenReasonPlaceholder')}
                        disabled={isProcessing}
                    />
                </FormField>
                {isOtherSelected && (
                    <FormField label={getString('bulkOrders.submittedOrders.reopenReasonOtherFieldLabel')}>
                        <Textarea
                            data-testid="reopen-bulk-order-reason-other"
                            value={otherReason}
                            onChange={({ detail }) => setOtherReason(detail.value)}
                            placeholder={getString('bulkOrders.submittedOrders.reopenReasonOtherPlaceholder')}
                            disabled={isProcessing}
                        />
                    </FormField>
                )}
            </SpaceBetween>
        </Modal>
    );
};
